import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft } from "lucide-react";
import { motion } from "framer-motion";
import { VoterInfo, FormErrors, SwipeOption, VoteType } from "./utils/types";
import { formatDate, formatTime } from "./utils/dateUtils";
import { triggerHaptic } from "./utils/voteUtils";
import VoteSummary from "./VoteSummary"; // Importer le composant VoteSummary
import { logger } from "@core/lib/logger";

interface VoterFormProps {
  options: SwipeOption[];
  votes: Record<string, VoteType>;
  userHasVoted: Record<string, boolean>;
  onBack: () => void;
  onCancel?: () => void;
  onSubmit: (voterInfo: VoterInfo) => Promise<void>;
  isSubmitting: boolean;
  getVoteText: (vote: VoteType) => string;
  setVoterInfo?: (info: VoterInfo) => void;
  voterInfo?: VoterInfo;
  formErrors?: Record<string, string>;
  isRestricted?: boolean;
}

export const VoterForm: React.FC<VoterFormProps> = ({
  options,
  votes,
  userHasVoted,
  onBack,
  onCancel,
  onSubmit,
  isSubmitting,
  getVoteText,
  setVoterInfo,
  voterInfo,
  formErrors,
  isRestricted = false,
}) => {
  const { t } = useTranslation("polls");
  // État local pour les informations de l'utilisateur
  const [localVoterInfo, setLocalVoterInfo] = useState<VoterInfo>(
    voterInfo || { name: "", email: "" },
  );
  const [localFormErrors, setLocalFormErrors] = useState<FormErrors>({});

  // Refs pour la gestion du focus sur la première erreur
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  // État pour la case à cocher "Recevoir par email"
  const [wantToReceiveEmail, setWantToReceiveEmail] = useState<boolean>(true);

  // Validation du formulaire
  const validateForm = () => {
    const errors: { name?: string; email?: string } = {};

    // Validation du nom - Toujours requis mais secondaire en restreint
    if (!localVoterInfo.name.trim()) {
      errors.name = t("voter_form.error_name_required");
    } else if (localVoterInfo.name.trim().length < 2) {
      errors.name = t("voter_form.error_name_too_short");
    }

    // Validation de l'email - Obligatoire si restreint, ou si option cochée
    // (case cochée par défaut : décocher ou remplir, pas de troisième état silencieux)
    if (isRestricted || wantToReceiveEmail) {
      if (!localVoterInfo.email.trim()) {
        errors.email = isRestricted
          ? t("voter_form.error_email_required_restricted")
          : t("voter_form.error_email_required_receive");
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(localVoterInfo.email.trim())) {
          errors.email = t("voter_form.error_email_invalid");
        }
      }
    }

    setLocalFormErrors(errors);
    // Déplacer le focus vers le premier champ en erreur pour l'accessibilité
    if (errors.name) {
      nameInputRef.current?.focus();
    } else if (errors.email) {
      emailInputRef.current?.focus();
    }
    return Object.keys(errors).length === 0;
  };

  // Nettoyer les erreurs quand l'utilisateur tape
  const handleNameChange = (value: string) => {
    const updatedInfo = { ...localVoterInfo, name: value };
    setLocalVoterInfo(updatedInfo);
    // Mettre à jour les informations dans le service parent
    if (setVoterInfo) {
      setVoterInfo(updatedInfo);
    }
    if (localFormErrors.name && value.trim().length >= 2) {
      setLocalFormErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleEmailChange = (value: string) => {
    const updatedInfo = { ...localVoterInfo, email: value };
    setLocalVoterInfo(updatedInfo);
    // Mettre à jour les informations dans le service parent
    if (setVoterInfo) {
      setVoterInfo(updatedInfo);
    }
    if (localFormErrors.email && value.includes("@") && value.includes(".")) {
      setLocalFormErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async () => {
    // Validation avant soumission
    if (!validateForm()) {
      triggerHaptic("heavy");
      return;
    }

    setLocalFormErrors({});

    // Mettre à jour les informations dans le service parent une dernière fois
    const submittedInfo: VoterInfo = {
      ...localVoterInfo,
      wantToReceiveEmail,
    };
    if (setVoterInfo) {
      setVoterInfo(submittedInfo);
    }

    try {
      logger.debug("VoterForm - Soumission", "vote", {
        voterName: localVoterInfo.name,
      });
      await onSubmit(submittedInfo);
    } catch (error) {
      logger.error("Erreur lors de la soumission", "vote", error);
      setLocalFormErrors({
        general:
          error instanceof Error && error.message
            ? error.message
            : t("voter_form.error_generic"),
      });
      triggerHaptic("heavy");
    }
  };

  return (
    <>
      {/* Backdrop semi-transparent */}
      <div className="fixed inset-0 bg-black/70 z-40" onClick={onBack} />

      {/* Modal centré */}
      <div className="fixed inset-0 flex items-center justify-center p-4 z-50 pointer-events-none">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="bg-card rounded-3xl w-full max-w-md max-h-[85vh] shadow-2xl border border-border pointer-events-auto flex flex-col overflow-hidden"
        >
          {/* Zone live pour annoncer les erreurs aux technologies d'assistance */}
          <div
            className="sr-only"
            aria-live="polite"
            id="voter-form-live-region"
          >
            {localFormErrors.general ||
              localFormErrors.name ||
              localFormErrors.email ||
              ""}
          </div>
          {/* Header avec bouton retour */}
          <div className="flex items-center justify-between p-8 pb-0 shrink-0">
            <button
              onClick={onBack}
              className="w-10 h-10 bg-muted rounded-full flex items-center justify-center text-muted-foreground hover:bg-accent transition-colors"
              data-testid="voter-form-back"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h2 className="text-xl font-bold text-foreground">
              {t("voter_form.title")}
            </h2>
            <div className="w-10 h-10"></div> {/* Spacer pour centrage */}
          </div>

          {/* Zone scrollable : résumé des votes + formulaire */}
          <div className="overflow-y-auto flex-1 min-h-0 px-8 pt-6">
            <VoteSummary
              options={options}
              votes={votes} // Utiliser les votes courants de l'utilisateur, pas les votes stockés
              userHasVoted={userHasVoted}
              getVoteText={getVoteText}
            />

            {/* Formulaire - Ordre inversé si restreint */}
            <div className="space-y-4 mb-6">
              {/* Email field - First if restricted */}
              {(isRestricted || wantToReceiveEmail) && (
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-foreground mb-2"
                  >
                    {t("voter_form.email_label")} {isRestricted && "*"}
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder={t("voter_form.email_placeholder")}
                    value={localVoterInfo.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className={`w-full p-4 border rounded-2xl text-lg transition-all bg-background text-foreground placeholder:text-muted-foreground ${
                      localFormErrors.email
                        ? "border-destructive focus:ring-2 focus:ring-destructive focus:border-destructive"
                        : "border-input focus:ring-2 focus:ring-ring focus:border-ring"
                    }`}
                    autoComplete="email"
                    disabled={isSubmitting}
                    aria-invalid={localFormErrors.email ? true : undefined}
                    aria-describedby={
                      localFormErrors.email ? "email-error" : undefined
                    }
                    ref={emailInputRef}
                  />
                  {localFormErrors.email && (
                    <p
                      className="text-destructive text-sm mt-1"
                      id="email-error"
                    >
                      {localFormErrors.email}
                    </p>
                  )}
                </div>
              )}

              {/* Name field */}
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-foreground mb-2"
                >
                  {t("voter_form.name_label")} *
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder={t("voter_form.name_placeholder")}
                  value={localVoterInfo.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={`w-full p-4 border rounded-2xl text-lg transition-all bg-background text-foreground placeholder:text-muted-foreground ${
                    localFormErrors.name
                      ? "border-destructive focus:ring-2 focus:ring-destructive focus:border-destructive"
                      : "border-input focus:ring-2 focus:ring-ring focus:border-ring"
                  }`}
                  autoComplete="name"
                  disabled={isSubmitting}
                  data-testid="voter-name"
                  aria-invalid={localFormErrors.name ? true : undefined}
                  aria-describedby={
                    localFormErrors.name ? "name-error" : undefined
                  }
                  ref={nameInputRef}
                />
                {localFormErrors.name && (
                  <p className="text-destructive text-sm mt-1" id="name-error">
                    {localFormErrors.name}
                  </p>
                )}
              </div>

              {!isRestricted && (
                <div className="flex items-center mb-4">
                  <input
                    id="receive-email"
                    type="checkbox"
                    checked={wantToReceiveEmail}
                    onChange={(e) => setWantToReceiveEmail(e.target.checked)}
                    className="w-5 h-5 text-primary rounded focus:ring-ring bg-background border-input"
                    disabled={isSubmitting}
                  />
                  <label
                    htmlFor="receive-email"
                    className="ml-2 text-sm font-medium text-foreground"
                  >
                    {t("voter_form.receive_email")}
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Validation et soumission */}
          <div className="space-y-3 p-8 pt-4 shrink-0 border-t border-border">
            {localFormErrors.general && (
              <div className="bg-destructive/10 border border-destructive rounded-xl p-3">
                <p className="text-destructive text-sm text-center">
                  {localFormErrors.general}
                </p>
              </div>
            )}

            {formErrors?.submit && formErrors.submit.includes("409") && (
              <div className="bg-warning-bg border border-warning rounded-xl p-3 mb-3">
                <p className="text-warning text-sm text-center">
                  {t("voter_form.already_voted")}
                </p>
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`w-full py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all ${
                isSubmitting
                  ? "bg-muted text-muted-foreground cursor-not-allowed"
                  : "bg-primary hover:bg-primary/90 text-primary-foreground"
              }`}
              data-testid="submit-votes"
            >
              {isSubmitting
                ? t("voter_form.submitting")
                : t("voter_form.submit")}
            </button>

            <p className="text-xs text-muted-foreground text-center mt-3">
              {t("voter_form.privacy_notice")}{" "}
              <a
                href="/docs/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              >
                {t("voter_form.learn_more")}
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default VoterForm;
